import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import AdminPage from "./features/admin/pages/AdminPage";
import App from "./App";
import { Cohorts } from "./features/cohorts/pages/Cohorts";
import "./index.css";
import NoteById from "./features/notes/pages/NoteById";
import { AddNewNote } from "./features/notes/pages/AddNewNote";
import { Notes } from "./features/notes/pages/Notes";
import { UploadDocumentPage } from "./features/notes/pages/UploadDocument";
import { Documents } from "./features/documents/pages/Documents";
import DocumentById from "./features/documents/pages/DocumentById";
import { CLOUD_AUTH_ROUTES } from "./features/auth/cloud/routes";
import UserProfile from "./features/auth/cloud/UserProfile";
import reportWebVitals from "./reportWebVitals";
import StudiesPage from "./features/studies/pages/StudiesPage";
import StudyPage from "./features/studies/pages/StudyPage";
import {
  AuthenticationQuartzClient,
  SimpleAuthenticationService,
  SimpleUserInfoProvider,
} from "./shared/services/api/onprem/auth";
import {
  IdentityProviderClientContext,
  SimpleAuthenticationServiceContext,
  UserInfoProviderContext,
} from "./shared/services/api/contexts";
import ErrorPage from "./features/auth/Error";
import { ONPREM_AUTH_ROUTES } from "./features/auth/onprem/routes";
import SimpleUserProfile from "./features/auth/onprem/SimpleUserProfile";
import {
  AWSCognitoUserInfoProvider,
  IdentityProviderClient,
} from "./shared/services/api/cloud/auth";
import { Patients } from "./features/patients/pages/Patients";
import CohortBuilder from "./features/cohorts/pages/CohortBuilder";
import { ExportBuilder } from "./shared/components/export/ExportBuilder";
import { PatientById } from "./features/patients/pages/PatientById";
import RPMInclusion from "./features/rpm/pages/RPMInclusion";
import { HomeRedirect } from "./shared/components/navigation/HomeRedirect";

const router = createBrowserRouter([
  {
    path: "/auth/",
    errorElement: <ErrorPage />,
    children: window.CONFIG.auth.kind === "AWS_COGNITO" ? CLOUD_AUTH_ROUTES : ONPREM_AUTH_ROUTES,
  },
  {
    path: "/",
    errorElement: <ErrorPage />,
    element: <App />,
    children: [
      {
        path: "user/profile",
        element:
          window.CONFIG.auth.kind === "AWS_COGNITO" ? <UserProfile /> : <SimpleUserProfile />,
      },
      {
        path: "add-document",
        element: <UploadDocumentPage />,
      },
      {
        path: "add-note",
        element: <AddNewNote />,
      },
      {
        path: "",
        element: <HomeRedirect />,
      },
      {
        path: "cohorts",
        element: <Cohorts />,
      },
      {
        path: "studies",
        element: <StudiesPage />,
      },
      {
        path: "study/:id",
        element: <StudyPage />,
      },
      {
        path: "cohort/:id",
        element: <CohortBuilder />,
      },
      {
        path: "cohort/:id/export",
        element: <ExportBuilder />,
      },
      {
        path: "notes",
        element: <Notes />,
      },
      {
        path: "documents",
        element: <Documents />,
      },
      {
        path: "document/:id",
        element: <DocumentById />,
        errorElement: <ErrorPage />,
      },
      {
        path: "patients",
        element: <Patients />,
      },
      {
        path: "patient/:id",
        element: <PatientById />,
      },
      {
        path: "note/:id",
        element: <NoteById />,
        errorElement: <ErrorPage />,
      },
      {
        path: "rpm/inclusion",
        element: <RPMInclusion />,
      },
      {
        path: "admin",
        element: <AdminPage />,
      },
    ],
  },
]);

const CloudRoot: React.FC<{}> = () => {
  const [authState, setAuthState] = React.useState<any>(IdentityProviderClient.getInitialState());
  const [loadingUser, setLoadingUser] = React.useState<boolean>(true);

  const identityProviderClient = React.useMemo(
    () => new IdentityProviderClient(authState, setAuthState),
    [authState, setAuthState]
  );
  const userInfoProvider = React.useMemo(
    () => new AWSCognitoUserInfoProvider(identityProviderClient),
    [identityProviderClient]
  );

  React.useEffect(() => {
    if (window.DISABLE_AUTH) {
      setLoadingUser(false);
    } else {
      IdentityProviderClient.initialize(setAuthState).finally(() => {
        setLoadingUser(false);
      });
    }
  }, [setLoadingUser, setAuthState]);

  if (loadingUser) {
    return <></>;
  }

  return (
    <React.StrictMode>
      <IdentityProviderClientContext.Provider value={identityProviderClient}>
        <UserInfoProviderContext.Provider value={userInfoProvider}>
          <RouterProvider router={router} />
        </UserInfoProviderContext.Provider>
      </IdentityProviderClientContext.Provider>
    </React.StrictMode>
  );
};

const OnpremRoot: React.FC<{}> = () => {
  const [authState, setAuthState] = React.useState<any>(
    SimpleAuthenticationService.getInitialState()
  );
  const authenticationService = new SimpleAuthenticationService(
    new AuthenticationQuartzClient(
      window.CONFIG.backendUrl,
      SimpleAuthenticationService.authorizationHeader
    ),
    authState,
    setAuthState
  );
  const userInfoProvider = new SimpleUserInfoProvider(authenticationService);

  return (
    <React.StrictMode>
      <SimpleAuthenticationServiceContext.Provider value={authenticationService}>
        <UserInfoProviderContext.Provider value={userInfoProvider}>
          <RouterProvider router={router} />
        </UserInfoProviderContext.Provider>
      </SimpleAuthenticationServiceContext.Provider>
    </React.StrictMode>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

if (window.CONFIG.auth.kind === "AWS_COGNITO") {
  root.render(<CloudRoot />);
} else {
  root.render(<OnpremRoot />);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
